import { useCallback, useEffect, useState } from "react";
import { workspaceKnowledgeApi } from "@/api/workspaceKnowledgeApi";

function formatListError(e) {
  if (!(e instanceof Error)) {
    return "Could not load collections.";
  }
  const status = e.status;
  const detail = e.data?.detail;
  const code =
    (detail && typeof detail === "object" && detail.error) ||
    e.data?.error ||
    null;
  if (code === "knowledge_base_database_error" || status === 503) {
    return (
      (detail && typeof detail === "object" && detail.message) ||
      "Knowledge base database is unavailable. Check tenant database configuration."
    );
  }
  if (e.message.includes("timed out")) {
    return e.message;
  }
  return e.message || "Could not load collections.";
}

/**
 * @returns {{
 *   collections: Array<{ id: string, display_name: string, description?: string, current_revision_id?: string | null }>,
 *   loading: boolean,
 *   error: string | null,
 *   isEmpty: boolean,
 *   refresh: () => Promise<void>,
 * }}
 */
export function useKnowledgeCollections() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await workspaceKnowledgeApi.listCollections();
      setCollections(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(formatListError(e));
      setCollections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isEmpty = !loading && !error && collections.length === 0;

  return { collections, loading, error, isEmpty, refresh };
}
